import { useMemo, useState } from 'react';
import { HiOutlineSwitchVertical } from 'react-icons/hi';
import { LuUser } from 'react-icons/lu';

import { CalendarIcon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { useAuthContext } from 'views/auth/Auth';
import { useHasConnectedCalendar } from 'views/calendar/calendar-queries';
import { ConnectCalendarCard } from 'views/calendar/widgets/ConnectCalendarCard';

import { DashboardCard } from '../DashboardCard';
import { useAllPriorities } from './priority-hooks';
import { useCompletedPriorities, useCompleteEventPriority, useCompleteTaskPriority } from './priority-queries';
import { Priority, PriorityType } from './priority-types';
import {
    getCompletionKeys,
    getCompletionTypeForPriority,
    getPriorityCompletionKeyForPriority,
    getPriorityKey,
    getPriorityTargetGUID,
    isAssignedToMe,
    sortPrioritiesByUrgency,
    sortPrioritiesChronologically,
} from './priority-utils';
import { PriorityItem } from './PriorityItem';

export const PriorityList = () => {
    const { data: priorities, isPending } = useAllPriorities();
    const hasConnectedCalendar = useHasConnectedCalendar();
    const { user } = useAuthContext();
    const completeEventPriority = useCompleteEventPriority();
    const completeTaskPriority = useCompleteTaskPriority();
    const [urgentActive, setUrgentActive] = useState(false);
    const [assignedToMeActive, setAssignedToMeActive] = useState(false);
    const [hiddenPriorityKeys, setHiddenPriorityKeys] = useState<Set<string>>(new Set());
    const [pendingPriorityKeys, setPendingPriorityKeys] = useState<Set<string>>(new Set());

    const targetGUIDs = useMemo(() => Array.from(new Set((priorities ?? []).map(getPriorityTargetGUID))), [priorities]);
    const completedPriorities = useCompletedPriorities(targetGUIDs);
    const completionKeys = useMemo(() => getCompletionKeys(completedPriorities.data ?? []), [completedPriorities.data]);

    const displayedPriorities = useMemo(() => {
        if (!priorities) return [];

        const filteredPriorities = priorities.filter(
            (priority) =>
                (!assignedToMeActive || isAssignedToMe(priority, user?.personOrgGUID)) &&
                !hiddenPriorityKeys.has(getPriorityKey(priority)) &&
                !completionKeys.has(getPriorityCompletionKeyForPriority(priority)),
        );

        return urgentActive
            ? sortPrioritiesByUrgency(filteredPriorities)
            : sortPrioritiesChronologically(filteredPriorities);
    }, [assignedToMeActive, completionKeys, hiddenPriorityKeys, priorities, urgentActive, user?.personOrgGUID]);

    const isLoading = hasConnectedCalendar.isPending || isPending || completedPriorities.isLoading;

    const setPriorityPending = (priorityKey: string, pending: boolean) => {
        setPendingPriorityKeys((currentKeys) => {
            const nextKeys = new Set(currentKeys);

            if (pending) {
                nextKeys.add(priorityKey);
            } else {
                nextKeys.delete(priorityKey);
            }

            return nextKeys;
        });
    };

    const hidePriority = (priorityKey: string) => {
        setHiddenPriorityKeys((currentKeys) => new Set(currentKeys).add(priorityKey));
    };

    const handleComplete = async (priority: Priority) => {
        const priorityKey = getPriorityKey(priority);
        if (pendingPriorityKeys.has(priorityKey)) {
            return;
        }

        setPriorityPending(priorityKey, true);

        try {
            if (priority.type === PriorityType.TASK) {
                await completeTaskPriority.mutateAsync({ taskGUID: priority.data.taskGUID });
            } else {
                await completeEventPriority.mutateAsync({
                    eventGUID: priority.data.eventGUID,
                    type: getCompletionTypeForPriority(priority),
                });
            }

            hidePriority(priorityKey);
        } finally {
            setPriorityPending(priorityKey, false);
        }
    };

    return (
        <DashboardCard
            size="full"
            className="flex flex-col border-0 p-0 py-6"
            title="Today's Priorities"
            titleRight={
                <div className="flex items-center gap-2">
                    <Button
                        variant={urgentActive ? 'highlighted' : 'ghost2'}
                        size="sm"
                        onClick={() => setUrgentActive((b) => !b)}
                        className="gap-1.5 text-base"
                    >
                        <HiOutlineSwitchVertical className="h-4 w-4" />
                        Urgent
                    </Button>
                    <Button
                        variant={assignedToMeActive ? 'highlighted' : 'ghost2'}
                        size="sm"
                        onClick={() => setAssignedToMeActive((b) => !b)}
                        className="gap-1.5 text-base"
                    >
                        <LuUser className="h-4 w-4" />
                        Assigned to Me
                    </Button>
                </div>
            }
        >
            <div className="max-h-[400px] w-full overflow-y-auto">
                {isLoading ? (
                    <LoadingState />
                ) : hasConnectedCalendar.data === false ? (
                    <ConnectCalendarCard />
                ) : displayedPriorities.length > 0 ? (
                    <div>
                        {displayedPriorities.map((priority) => (
                            <PriorityItem
                                key={getPriorityKey(priority)}
                                priority={priority}
                                onComplete={() => void handleComplete(priority)}
                                isCompleting={pendingPriorityKeys.has(getPriorityKey(priority))}
                            />
                        ))}
                    </div>
                ) : (
                    <EmptyState />
                )}
            </div>
        </DashboardCard>
    );
};

const LoadingState = () => {
    return (
        <div>
            {[1, 2, 3].map((i) => (
                <div key={i} className="border-border flex animate-pulse items-center gap-6 border-b py-4">
                    <div className="h-12 w-12 rounded-full bg-gray-200"></div>
                    <div className="flex-1 space-y-2">
                        <div className="h-4 w-1/3 rounded bg-gray-200"></div>
                        <div className="h-3 w-1/4 rounded bg-gray-200"></div>
                    </div>
                    <div className="flex gap-2">
                        <div className="h-10 w-24 rounded-full bg-gray-200"></div>
                        <div className="h-10 w-10 rounded-full bg-gray-200"></div>
                        <div className="h-10 w-10 rounded-full bg-gray-200"></div>
                    </div>
                </div>
            ))}
        </div>
    );
};

const EmptyState = () => {
    return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <CalendarIcon className="mb-2 h-8 w-8 text-gray-400" />
            <p className="text-sm font-medium">No priorities at the moment</p>
            <p className="text-xs">You&apos;re all caught up!</p>
        </div>
    );
};
